import { config } from "@/config/rainbow.config";
import { ChainData } from "@/constants/chain-details";
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import type { CheckoutStatusType } from "@/features/bridge/types/checkout.types";
import type { HexString } from "@/types/general.types";
import { useUIStore } from "@/stores/ui-states.store";

/**
 * This hook provides functionality to manage and fetch balances, swap chains,
 * and handle the state related to bridging operations. It also performs validation
 * on the active network and manages the loading states for balance retrieval.
 */
const useBridgeControls = () => {
  // const {
  //   isValidNetwork,
  //   setIsValidNetwork,
  //   isBridgePending,
  //   isDestinationChainLoading,
  //   isOriginChainLoading,
  //   setIsBridgePending,
  //   setIsDestinationChainLoading,
  //   setIsOriginChainLoading,
  // } = useBridgeStatusStore();

  // const {
  //   amount,
  //   setAmount,
  //   destinationChain,
  //   destinationChainBalance,
  //   originChain,
  //   originChainBalance,
  //   setDestinationChain,
  //   setDestinationChainBalance,
  //   setOriginChain,
  //   setOriginChainBalance,
  //   swapChains,
  // } = useBridgeInputStore();

  /**
   * Gets the account data from the current provider
   */
  const account = useAccount();

  // The state of the network and whether it is valid or not depending on the wagmi configs accepted chain args
  const [isValidNetwork, setIsValidNetwork] = useState(false);

  // States for managing the loading state for the balance retrieval
  const [isDestinationChainLoading, setIsDestinationChainLoading] =
    useState(false);
  const [isOriginChainLoading, setIsOriginChainLoading] = useState(false);

  // A state to manage the entered amount input value in decimals.
  const [amount, setAmount] = useState<string>(""); // TODO This is no longer needed as we will be tracking our state through the standard token store

  // A state for managing the pending signing process. Wagmi provides a state for this but it has a undesirable delay
  // const [isBridgePending, setIsBridgePending] = useState(false);
  const { isBridgePending, setIsBridgePending } = useUIStore((state) => ({
    isBridgePending: state.isSigningPending,
    setIsBridgePending: state.setIsSigningPending,
  }));

  // The states for the currently selected origin and destination chain
  const [originChain, setOriginChain] = useState<ChainData | undefined>(
    undefined,
  );
  const [destinationChain, setDestinationChain] = useState<
    ChainData | undefined
  >(undefined);

  // The states for the current balances retrieved for the origin and destination chain
  const [originChainBalance, setOriginChainBalance] = useState<
    string | undefined
  >(undefined);
  const [destinationChainBalance, setDestinationChainBalance] = useState<
    string | undefined
  >(undefined);

  const [checkoutLoadingStatus, setCheckoutLoadingStatus] =
    useState<CheckoutStatusType | null>(null);
  const [checkoutLoadingText, setCheckoutLoadingText] = useState("");

  // State for managing active pending txns
  const [pendingTxns, setPendingTxns] = useState<HexString[]>([]);

  /**
   * Fetches and updates the balance for the origin chain.
   *
   * This function checks if the account address and the active origin chain are defined. It then sets
   * the loading state to `true` while fetching the balance for the given account and chain. Upon
   * successful retrieval, it formats the balance and updates the state with the formatted value.
   *
   * @async
   * @function fetchOriginBalance
   * @returns {Promise<boolean>} Returns a boolean value indicating whether the balance was fetched correctly
   * on the fetched balance.
   *
   * @throws {Error} Throws an error if the balance fetching fails, though this function handles errors internally.
   */
  // const fetchOriginBalance = useCallback(async () => {
  //   if (account.address && originChain) {
  //     setIsOriginChainLoading(true);
  //     const toBalance = await getBalance(originChain.chainId, account.address);
  //     if (toBalance !== undefined) {
  //       setIsOriginChainLoading(false);
  //       const formattedBalance = ethers.formatUnits(toBalance, 18);
  //       setOriginChainBalance(formattedBalance);
  //       return true;
  //     } else {
  //       return false;
  //     }
  //   }
  // }, [
  //   account.address,
  //   originChain,
  //   setIsOriginChainLoading,
  //   setOriginChainBalance,
  // ]);

  /**
   * Fetches and updates the balance for the destination chain.
   *
   * This function checks if the account address and the active destination chain are defined. It then sets
   * the loading state to `true` while fetching the balance for the given account and chain. Upon
   * successful retrieval, it formats the balance and updates the state with the formatted value.
   *
   * @async
   * @function fetchDestinationBalance
   * @returns {Promise<boolean>} Returns a boolean value indicating whether the balance was fetched correctly
   * on the fetched balance.
   *
   * @throws {Error} Throws an error if the balance fetching fails, though this function handles errors internally.
   */
  // const fetchDestinationBalance = useCallback(async () => {
  //   if (account.address && destinationChain) {
  //     setIsDestinationChainLoading(true);
  //     const toBalance = await getBalance(
  //       destinationChain.chainId,
  //       account.address
  //     );
  //     if (toBalance !== undefined) {
  //       setIsDestinationChainLoading(false);
  //       const formattedBalance = ethers.formatUnits(toBalance, 18);
  //       setDestinationChainBalance(formattedBalance);
  //       return true;
  //     } else return false;
  //   }
  // }, [
  //   account.address,
  //   destinationChain,
  //   setDestinationChainBalance,
  //   setIsDestinationChainLoading,
  // ]);

  /**
   * Fetches the balances for both the origin and destination chains.
   *
   * This function calls `fetchOriginBalance` to retrieve and update the balance for the origin chain,
   * and `fetchDestinationBalance` to do the same for the destination chain.
   *
   * @function fetchBalances
   * @returns {Promise<void>} This function does not return a value. It triggers updates for both origin and
   * destination balances by calling the respective functions.
   */
  // const fetchBalances = async (): Promise<void> => {
  //   await fetchOriginBalance();
  //   await fetchDestinationBalance();
  // };

  /**
   * Swaps the active origin and destination chains.
   *
   * This function temporarily stores the current `originChain` in a variable, sets `originChain`
   * to `destinationChain`, and then sets `destinationChain` to the previously stored value. This effectively
   * swaps the origin and destination chains.
   *
   * @function swapChains
   * @returns {void} This function does not return a value. It updates the state of `originChain`
   * and `destinationChain` by swapping their values.
   */
  const swapChains = (): void => {
    const temp = originChain;
    setOriginChain(destinationChain);
    setDestinationChain(temp);
  };

  /**
   * Sets the amount to the maximum balance available on the origin chain.
   *
   * This function updates the amount state variable to the current value of `originChainBalance` if it is defined.
   * It is typically used to quickly set the amount input to the maximum balance available for transactions.
   *
   * @function setMaxAmount
   * @returns {void} This function does not return a value. It updates the `amount` state variable with
   * the value of `originChainBalance`.
   */
  const setMaxAmount = (): void => {
    if (originChainBalance) setAmount(originChainBalance);
  };

  // Fetches the balance for the origin chain on mount and sets a polling interval
  // useEffect(() => {
  //   fetchOriginBalance();
  //   const interval = setInterval(fetchOriginBalance, BALANCE_POLLING_INTERVAL);
  //   return () => clearInterval(interval);
  // }, [account.address, originChain, fetchOriginBalance]);

  // Fetches the balance for the destination chain on mount and sets a polling interval
  // useEffect(() => {
  //   fetchDestinationBalance();
  //   const interval = setInterval(
  //     fetchDestinationBalance,
  //     BALANCE_POLLING_INTERVAL
  //   );
  //   return () => clearInterval(interval);
  // }, [account.address, destinationChain, fetchDestinationBalance]);

  // Checks to see whether the currently active chain id is whitelisted in the config
  useEffect(() => {
    const isValid = config.chains.some((chain) => chain.id === account.chainId);
    setIsValidNetwork(isValid);
  }, [account.chainId, setIsValidNetwork]);

  const fromChain = {
    originChain,
    originChainBalance,
    setOriginChain,
    isOriginChainLoading,
  };

  const toChain = {
    destinationChain,
    destinationChainBalance,
    setDestinationChain,
    isDestinationChainLoading,
  };

  return {
    fromChain,
    toChain,
    swapChains,
    amount,
    setAmount,
    setMaxAmount,
    account,
    isValidNetwork,
    isBridgePending,
    setIsBridgePending,
    checkoutLoadingStatus,
    setCheckoutLoadingStatus,
    checkoutLoadingText,
    setCheckoutLoadingText,
    // fetchOriginBalance,
    // fetchDestinationBalance,
    // fetchBalances,
    pendingTxns,
    setPendingTxns,
  };
};

export default useBridgeControls;
